import { useState, useEffect, useRef, useCallback } from "react";
import config from "../config";

const api = config.API_BASE_URL;
const chatMgmt = config.API_BASE_URL;

// ─── Helpers ──────────────────────────────────────────────────
const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
const formatDay = (ts) => {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

// ─── Profile Picture Cache (blob URLs, per session) ───────────
const PIC_CACHE = {};

async function fetchProfilePic(username) {
  if (!username) return null;
  if (PIC_CACHE[username] !== undefined) return PIC_CACHE[username];
  PIC_CACHE[username] = null; // mark as fetching
  try {
    const res = await fetch(`${api}/Users/get-profile-picture/${encodeURIComponent(username)}`);
    if (!res.ok) { PIC_CACHE[username] = null; return null; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    PIC_CACHE[username] = url;
    return url;
  } catch {
    PIC_CACHE[username] = null;
    return null;
  }
}

const REACTIONS = [
  { emoji: "❤️", label: "Love",  color: "#f33e58" },
  { emoji: "😂", label: "Haha",  color: "#f7b125" },
  { emoji: "😮", label: "Wow",   color: "#f7b125" },
  { emoji: "😢", label: "Sad",   color: "#f7b125" },
  { emoji: "😡", label: "Angry", color: "#e9710f" },
  { emoji: "👍", label: "Like",  color: "#0084ff" },
];

// ─── Avatar with profile picture ──────────────────────────────
function Avatar({ name, size = 38, online = false, picUrl = null }) {
  const COLORS = ["#0084ff","#00b09b","#f7971e","#e040fb","#f44336","#43a047","#ff5722","#3f51b5"];
  const color = COLORS[name?.charCodeAt(0) % COLORS.length] ?? "#0084ff";
  const [err, setErr] = useState(false);

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      {picUrl && !err ? (
        <img src={picUrl} alt={name} onError={() => setErr(true)}
          style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover",
            display: "block", border: "2.5px solid rgba(255,255,255,0.12)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }} />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: "50%",
          background: `linear-gradient(135deg, ${color}, ${color}bb)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 800, fontSize: size * 0.38,
          userSelect: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          border: "2.5px solid rgba(255,255,255,0.1)",
        }}>
          {name?.[0]?.toUpperCase() ?? "?"}
        </div>
      )}
      {online && (
        <span style={{
          position: "absolute", bottom: 1, right: 1,
          width: size * 0.27, height: size * 0.27, borderRadius: "50%",
          background: "#31a24c",
          border: "2px solid #0f1117",
          boxShadow: "0 0 0 2px rgba(49,164,76,0.35)",
          animation: "pulse-green 2s infinite",
        }} />
      )}
    </div>
  );
}

// ─── Reaction Picker ──────────────────────────────────────────
function ReactionPicker({ onPick, onClose, isAdmin, currentReaction, isDark }) {
  return (
    <div onMouseLeave={onClose} style={{
      position: "absolute",
      bottom: "calc(100% + 10px)",
      [isAdmin ? "right" : "left"]: -8,
      background: isDark
        ? "rgba(22,25,40,0.95)"
        : "rgba(255,255,255,0.98)",
      backdropFilter: "blur(20px)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 999,
      padding: "8px 14px",
      display: "flex", gap: 4,
      boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.6)" : "0 12px 40px rgba(0,0,0,0.18)",
      zIndex: 400,
      animation: "pop-spring 0.22s cubic-bezier(.34,1.56,.64,1)",
    }}>
      {REACTIONS.map(({ emoji, label, color }) => {
        const isActive = currentReaction === emoji;
        return (
          <div key={emoji} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button
              onClick={() => { onPick(emoji); onClose(); }}
              title={label}
              style={{
                background: isActive ? `${color}22` : "none",
                border: "none", cursor: "pointer",
                fontSize: 26, padding: "4px", borderRadius: "50%",
                width: 46, height: 46,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "transform 0.18s cubic-bezier(.34,1.56,.64,1)",
                transform: isActive ? "scale(1.3)" : "scale(1)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "scale(1.55) translateY(-8px)";
                e.currentTarget.parentNode.querySelector(".tip").style.opacity = "1";
                e.currentTarget.parentNode.querySelector(".tip").style.transform = "translateX(-50%) scale(1)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = isActive ? "scale(1.3)" : "scale(1)";
                e.currentTarget.parentNode.querySelector(".tip").style.opacity = "0";
                e.currentTarget.parentNode.querySelector(".tip").style.transform = "translateX(-50%) scale(0.7)";
              }}
            >{emoji}</button>
            <span className="tip" style={{
              position: "absolute", bottom: -22, left: "50%",
              transform: "translateX(-50%) scale(0.7)",
              background: "rgba(0,0,0,0.8)", color: "#fff",
              fontSize: 10, fontWeight: 700, padding: "3px 8px",
              borderRadius: 8, whiteSpace: "nowrap", opacity: 0,
              transition: "all 0.14s ease", pointerEvents: "none",
            }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Reply Preview Bar ────────────────────────────────────────
function ReplyBar({ replyTo, onCancel, isDark }) {
  if (!replyTo) return null;
  const isImg = replyTo.content?.startsWith("[IMAGE]");
  const preview = isImg ? "📷 Photo" : replyTo.content?.slice(0, 70);
  const clean = preview?.startsWith("↩") ? preview.replace(/^↩ ".*?" — /, "") : preview;
  return (
    <div style={{
      padding: "10px 18px",
      background: isDark ? "rgba(0,132,255,0.08)" : "rgba(0,132,255,0.05)",
      borderTop: "2px solid rgba(0,132,255,0.5)",
      display: "flex", alignItems: "center", gap: 10,
      animation: "slide-up 0.15s ease",
    }}>
      <div style={{ width: 3, borderRadius: 3, alignSelf: "stretch", background: "linear-gradient(180deg,#0084ff,#00c6ff)", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#0084ff", marginBottom: 1 }}>
          Replying to {replyTo.senderType === "admin" ? "yourself" : replyTo.user}
        </div>
        <div style={{ fontSize: 12, color: isDark ? "#9ca3af" : "#65676b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {clean}
        </div>
      </div>
      <button onClick={onCancel} style={{
        background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)",
        border: "none", borderRadius: "50%", width: 26, height: 26,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        color: isDark ? "#9ca3af" : "#65676b", fontSize: 13, fontWeight: 900,
        transition: "background 0.15s",
      }}>✕</button>
    </div>
  );
}

// ─── Quoted Snippet inside bubble ─────────────────────────────
function ReplySnippet({ quotedContent, isAdmin, isDark, activeUser, quotedSenderType }) {
  const isImg = quotedContent?.startsWith("[IMAGE]");
  const text = isImg ? "📷 Photo" : quotedContent?.slice(0, 80);
  const label = quotedSenderType === "admin" ? "You" : (activeUser || "User");
  return (
    <div style={{
      borderLeft: `3px solid ${isAdmin ? "rgba(255,255,255,0.5)" : "#0084ff"}`,
      paddingLeft: 8, marginBottom: 6,
      borderRadius: "2px 4px 4px 2px",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: isAdmin ? "rgba(255,255,255,0.85)" : "#0084ff", marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 11.5, color: isAdmin ? "rgba(255,255,255,0.72)" : (isDark ? "#9ca3af" : "#65676b"), lineHeight: 1.35 }}>{text}</div>
    </div>
  );
}

// ─── Image Preview ────────────────────────────────────────────
function ImagePreview({ file, onRemove }) {
  const [url] = useState(() => URL.createObjectURL(file));
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <img src={url} alt="preview" style={{ height: 76, borderRadius: 12, border: "2px solid #0084ff", objectFit: "cover", display: "block" }} />
      <button onClick={onRemove} style={{
        position: "absolute", top: -7, right: -7,
        background: "#e41e3f", color: "#fff",
        border: "2.5px solid #0f1117", borderRadius: "50%",
        width: 22, height: 22, cursor: "pointer", fontSize: 11, fontWeight: 900,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 2px 8px rgba(228,30,63,0.5)",
      }}>✕</button>
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────
function MessageBubble({ msg, isAdmin, prevMsg, nextMsg, onReply, onDelete, onPin, onReact, isDark, pinnedId, activeUser, userPicUrl }) {
  const [hovered, setHovered] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const timerRef = useRef(null);

  const sameAuthorPrev = prevMsg?.senderType === msg.senderType;
  const sameAuthorNext = nextMsg?.senderType === msg.senderType;
  const showTime = !nextMsg || new Date(nextMsg.timestamp) - new Date(msg.timestamp) > 300000;
  const isPinned = String(pinnedId) === String(msg.messageId);

  const isImage = msg.content?.startsWith("[IMAGE]");
  const rawImg = isImage ? msg.content.replace("[IMAGE]", "").trim() : null;
  const imageUrl = rawImg ? (rawImg.startsWith("data:") || rawImg.startsWith("http") ? rawImg : `data:image/png;base64,${rawImg}`) : null;

  const isReply = !isImage && msg.content?.startsWith("↩");
  let quotedText = null, quotedSenderType = null, actualContent = msg.content;
  if (isReply) {
    const m = msg.content.match(/^↩ "(.*?)" — (.*)$/s);
    if (m) { quotedText = m[1]; actualContent = m[2]; quotedSenderType = isAdmin ? "admin" : "user"; }
  }

  const reaction = msg.reaction || (msg.likes ? "❤️" : null);

  return (
    <div style={{
      display: "flex",
      flexDirection: isAdmin ? "row-reverse" : "row",
      alignItems: "flex-end", gap: 8,
      marginTop: sameAuthorPrev ? 2 : 16,
      marginBottom: reaction ? 20 : 0,
      position: "relative",
      animation: "msg-in 0.2s cubic-bezier(.34,1.3,.64,1)",
    }}
      onMouseEnter={() => { setHovered(true); clearTimeout(timerRef.current); }}
      onMouseLeave={() => { setHovered(false); timerRef.current = setTimeout(() => setShowPicker(false), 300); }}
    >
      {/* Avatar */}
      {!isAdmin && (
        <div style={{ width: 30, flexShrink: 0, alignSelf: "flex-end" }}>
          {!sameAuthorNext ? <Avatar name={msg.user} size={30} online picUrl={userPicUrl} /> : <div style={{ width: 30 }} />}
        </div>
      )}

      <div style={{ maxWidth: "64%", display: "flex", flexDirection: "column", alignItems: isAdmin ? "flex-end" : "flex-start", position: "relative" }}>
        {isPinned && (
          <div style={{ fontSize: 10, color: "#f7971e", fontWeight: 700, marginBottom: 3, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ animation: "wiggle 2s infinite" }}>📌</span> Pinned message
          </div>
        )}

        <div style={{ display: "flex", flexDirection: isAdmin ? "row-reverse" : "row", alignItems: "center", gap: 6, position: "relative" }}>
          {/* Reaction picker anchor */}
          <div style={{ position: "relative" }}>
            {showPicker && (
              <ReactionPicker isAdmin={isAdmin} currentReaction={reaction}
                onPick={(emoji) => onReact(msg.messageId, emoji)}
                onClose={() => setShowPicker(false)} isDark={isDark} />
            )}
          </div>

          {/* Bubble */}
          <div style={{ position: "relative" }}>
            {isImage ? (
              <div onClick={() => window.open(imageUrl, "_blank")}
                style={{ borderRadius: 16, overflow: "hidden", maxWidth: 260, cursor: "pointer", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
                <img src={imageUrl} alt="shared" style={{ width: "100%", display: "block" }} />
              </div>
            ) : (
              <div style={{
                background: isAdmin
                  ? "linear-gradient(135deg, #0084ff, #006fde)"
                  : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"),
                backdropFilter: !isAdmin ? "blur(10px)" : "none",
                color: isAdmin ? "#fff" : (isDark ? "#e8e8f0" : "#0f0f1a"),
                borderRadius: isAdmin
                  ? `18px 4px ${sameAuthorNext ? "4px" : "18px"} 18px`
                  : `4px 18px 18px ${sameAuthorNext ? "4px" : "18px"}`,
                padding: quotedText ? "10px 15px 9px" : "10px 15px",
                fontSize: 14, lineHeight: 1.5, wordBreak: "break-word",
                boxShadow: isAdmin
                  ? "0 4px 15px rgba(0,132,255,0.35)"
                  : (isDark ? "0 2px 8px rgba(0,0,0,0.25)" : "0 2px 6px rgba(0,0,0,0.1)"),
                border: !isAdmin ? `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}` : "none",
                transition: "transform 0.1s",
                cursor: "default",
              }}
                onDoubleClick={() => onReply(msg)}>
                {quotedText && <ReplySnippet quotedContent={quotedText} quotedSenderType={quotedSenderType} isAdmin={isAdmin} isDark={isDark} activeUser={activeUser} />}
                {actualContent}
              </div>
            )}

            {/* Reaction badge */}
            {reaction && (
              <div onClick={() => onReact(msg.messageId, reaction)}
                style={{
                  position: "absolute", bottom: -16,
                  [isAdmin ? "left" : "right"]: 6,
                  background: isDark ? "rgba(20,20,35,0.95)" : "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                  borderRadius: 20, padding: "2px 8px 2px 5px",
                  fontSize: 13, boxShadow: "0 3px 12px rgba(0,0,0,0.2)",
                  zIndex: 5, cursor: "pointer", userSelect: "none",
                  display: "flex", alignItems: "center", gap: 4,
                  animation: "pop-spring 0.22s cubic-bezier(.34,1.56,.64,1)",
                }}>
                <span style={{ fontSize: 14 }}>{reaction}</span>
                <span style={{ fontSize: 11, color: isDark ? "#94a3b8" : "#65676b", fontWeight: 600 }}>1</span>
              </div>
            )}
          </div>

          {/* Hover actions */}
          {hovered && (
            <div style={{
              display: "flex", flexDirection: isAdmin ? "row-reverse" : "row",
              gap: 4, alignItems: "center",
              animation: "fade-in 0.1s ease",
            }}>
              {[
                { icon: "😊", title: "React", action: () => setShowPicker(v => !v), active: false },
                { icon: "↩", title: "Reply", action: () => onReply(msg), active: false },
                { icon: "📌", title: isPinned ? "Unpin" : "Pin", action: () => onPin(msg.messageId), active: isPinned },
                ...(isAdmin ? [{ icon: "🗑️", title: "Delete", action: () => onDelete(msg.messageId), danger: true }] : []),
              ].map((btn) => (
                <button key={btn.title} onClick={btn.action} title={btn.title}
                  style={{
                    background: btn.danger
                      ? (isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)")
                      : btn.active
                        ? "rgba(247,151,30,0.2)"
                        : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)"),
                    border: btn.active ? "1px solid rgba(247,151,30,0.5)" : "1px solid transparent",
                    borderRadius: "50%", width: 30, height: 30,
                    cursor: "pointer", fontSize: 14,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backdropFilter: "blur(10px)",
                    transition: "transform 0.12s, background 0.12s",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.18)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                >{btn.icon}</button>
              ))}
            </div>
          )}
        </div>

        {showTime && (
          <span style={{ fontSize: 10, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)", marginTop: reaction ? 20 : 5, padding: "0 6px" }}>
            {formatTime(msg.timestamp)}{isAdmin && msg.read && " · Seen ✓"}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Conversation Item ────────────────────────────────────────
function ConversationItem({ conv, active, onClick, isDark, picUrl }) {
  const unread = conv.unreadCount > 0;
  return (
    <div onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 11,
      padding: "8px 12px", borderRadius: 14, cursor: "pointer",
      background: active
        ? (isDark ? "rgba(0,132,255,0.15)" : "rgba(0,132,255,0.1)")
        : "transparent",
      borderLeft: `3px solid ${active ? "#0084ff" : "transparent"}`,
      transition: "all 0.15s",
      position: "relative",
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
      <Avatar name={conv.username} size={44} online={conv.online} picUrl={picUrl} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{
            fontWeight: unread ? 800 : 600, fontSize: 14,
            color: isDark ? "#f0f0f8" : "#0f0f1a",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130,
          }}>{conv.displayName || conv.username}</span>
          <span style={{ fontSize: 10, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)", flexShrink: 0, marginLeft: 4 }}>
            {conv.lastTimestamp ? formatDay(conv.lastTimestamp) : ""}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{
            fontSize: 12,
            color: unread ? (isDark ? "#c0c0d8" : "#0f0f1a") : (isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.45)"),
            fontWeight: unread ? 600 : 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
          }}>
            {conv.lastMessage?.startsWith("[IMAGE]") ? "📷 Photo" : (conv.lastMessage ?? "No messages yet")}
          </span>
          {unread && (
            <span style={{
              background: "linear-gradient(135deg,#0084ff,#006fde)",
              color: "#fff", borderRadius: "50%",
              width: 20, height: 20, fontSize: 10, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginLeft: 6,
              boxShadow: "0 2px 8px rgba(0,132,255,0.5)",
              animation: "pop-spring 0.3s ease",
            }}>{conv.unreadCount > 9 ? "9+" : conv.unreadCount}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Forward Modal ────────────────────────────────────────────
function ForwardModal({ username, currentAssignee, onForward, onClose, isDark }) {
  const [targetAdmin, setTargetAdmin] = useState("");
  const [forwarding, setForwarding] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [search, setSearch] = useState("");

  const isDarkBg = isDark ? "#0d1117" : "#ffffff";
  const borderC = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  useEffect(() => {
    fetch(`${api}/Admin`, { headers: { accept: "text/plain" } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAdmins(data.filter(a => a.username?.toLowerCase() !== "superadmin" && a.isApproved !== false)))
      .catch(() => {})
      .finally(() => setLoadingAdmins(false));
  }, []);

  const COLORS = ["#0084ff","#00b09b","#f7971e","#e040fb","#f44336","#43a047","#ff5722","#3f51b5"];
  const filtered = admins.filter(a =>
    a.username?.toLowerCase().includes(search.toLowerCase()) ||
    a.fullNames?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 99999, animation: "fade-in 0.2s ease",
    }}>
      <div style={{
        background: isDarkBg,
        border: `1px solid ${borderC}`,
        borderRadius: 24, width: 460, maxHeight: "88vh",
        boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        overflow: "hidden", display: "flex", flexDirection: "column",
        animation: "slide-up 0.25s cubic-bezier(.34,1.2,.64,1)",
      }}>
        {/* Header */}
        <div style={{
          padding: "22px 24px 18px",
          background: "linear-gradient(135deg, #0084ff 0%, #8b5cf6 100%)",
          position: "relative",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22,
            }}>⚡</div>
            <div>
              <h3 style={{ margin: 0, color: "#fff", fontSize: 17, fontWeight: 800 }}>Forward Conversation</h3>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Assign to another admin</p>
            </div>
          </div>
          <button onClick={onClose} style={{
            position: "absolute", top: 18, right: 18,
            background: "rgba(255,255,255,0.15)",
            border: "none", borderRadius: "50%",
            width: 32, height: 32, cursor: "pointer", color: "#fff", fontSize: 15,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
          >✕</button>
        </div>

        <div style={{ padding: "16px 20px", flex: 1, overflowY: "auto" }}>
          {/* Current user card */}
          <div style={{
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            border: `1px solid ${borderC}`, borderRadius: 14, padding: "12px 14px",
            marginBottom: 16, display: "flex", alignItems: "center", gap: 10,
          }}>
            <Avatar name={username} size={36} online />
            <div>
              <div style={{ color: isDark ? "#f0f0f8" : "#0f0f1a", fontWeight: 700, fontSize: 13 }}>{username}</div>
              <div style={{ fontSize: 11, color: currentAssignee ? "#f59e0b" : (isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)") }}>
                {currentAssignee ? `Currently: ${currentAssignee}` : "Unassigned"}
              </div>
            </div>
          </div>

          {/* Search */}
          <div style={{ position: "relative", marginBottom: 12 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, opacity: 0.5 }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search admin..." autoFocus
              style={{
                width: "100%", boxSizing: "border-box",
                background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                border: `1.5px solid ${borderC}`, borderRadius: 12,
                padding: "10px 14px 10px 36px", fontSize: 13,
                color: isDark ? "#f0f0f8" : "#0f0f1a",
                outline: "none", fontFamily: "inherit",
                transition: "border-color 0.15s",
              }}
              onFocus={e => e.target.style.borderColor = "#0084ff"}
              onBlur={e => e.target.style.borderColor = borderC}
            />
          </div>

          {/* Admin list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 270, overflowY: "auto" }}>
            {loadingAdmins ? (
              <div style={{ textAlign: "center", padding: 24, color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", fontSize: 13 }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid transparent", borderTop: "2px solid #0084ff", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
                Loading admins...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: 20, color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", fontSize: 13 }}>No admins found</div>
            ) : filtered.map(admin => {
              const isSelected = targetAdmin === admin.username;
              const isCurrent = admin.username === currentAssignee;
              const aColor = COLORS[admin.username?.charCodeAt(0) % COLORS.length] ?? "#0084ff";
              return (
                <div key={admin.username}
                  onClick={() => !isCurrent && setTargetAdmin(admin.username)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 14px", borderRadius: 12,
                    border: `1.5px solid ${isSelected ? "#0084ff" : borderC}`,
                    background: isSelected
                      ? (isDark ? "rgba(0,132,255,0.12)" : "rgba(0,132,255,0.07)")
                      : "transparent",
                    cursor: isCurrent ? "default" : "pointer",
                    opacity: isCurrent ? 0.55 : 1, transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (!isSelected && !isCurrent) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"; }}
                  onMouseLeave={e => { if (!isSelected && !isCurrent) e.currentTarget.style.background = "transparent"; }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: "50%",
                    background: admin.profilePicture ? "transparent" : `linear-gradient(135deg, ${aColor}, ${aColor}99)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 15, fontWeight: 700, color: "#fff", flexShrink: 0,
                    overflow: "hidden", border: isSelected ? `2px solid #0084ff` : "none",
                  }}>
                    {admin.profilePicture
                      ? <img src={`data:image/jpeg;base64,${admin.profilePicture}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" />
                      : (admin.fullNames?.[0] || admin.username?.[0] || "A")?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? "#0084ff" : (isDark ? "#f0f0f8" : "#0f0f1a") }}>
                      {admin.fullNames || admin.username}
                    </div>
                    <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>@{admin.username}</div>
                  </div>
                  {isCurrent && <span style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700 }}>Current</span>}
                  {isSelected && !isCurrent && <span style={{ background: "rgba(0,132,255,0.15)", color: "#0084ff", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700 }}>✓ Selected</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${borderC}`, display: "flex", gap: 10 }}>
          <button onClick={async () => { if (!targetAdmin) return; setForwarding(true); await onForward(targetAdmin); setForwarding(false); }}
            disabled={!targetAdmin || forwarding}
            style={{
              flex: 1, background: targetAdmin && !forwarding ? "linear-gradient(135deg,#0084ff,#006fde)" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
              color: targetAdmin && !forwarding ? "#fff" : (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"),
              border: "none", borderRadius: 14, padding: "13px 18px",
              fontSize: 14, fontWeight: 700, cursor: targetAdmin && !forwarding ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "all 0.2s",
              boxShadow: targetAdmin && !forwarding ? "0 4px 16px rgba(0,132,255,0.4)" : "none",
            }}>
            {forwarding
              ? <><div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 0.8s linear infinite" }} /> Forwarding...</>
              : "⚡ Forward & Assign"}
          </button>
          <button onClick={onClose} style={{
            background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
            color: isDark ? "#f0f0f8" : "#0f0f1a",
            border: "none", borderRadius: 14, padding: "13px 20px",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
            transition: "background 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}
            onMouseLeave={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"}
          >Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function AdminMessenger({ adminUsername: _adminUsername, adminRole: _adminRole }) {
  const adminData = (() => { try { return JSON.parse(localStorage.getItem("admin") || "{}"); } catch { return {}; } })();
  const adminUsername = _adminUsername || adminData?.username || adminData?.claimUsername || adminData?.identificationCode || null;
  const adminRole = _adminRole || localStorage.getItem("userRole") || adminData?.role || "admin";
  const isSuperAdmin = adminRole?.toLowerCase() === "superadmin";

  const [isDark, setIsDark] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [localReactions, setLocalReactions] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [search, setSearch] = useState("");
  const [newUserInput, setNewUserInput] = useState("");
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinnedMessageId, setPinnedMessageId] = useState(null);
  const [claimLoading, setClaimLoading] = useState(true);
  const [claimStatus, setClaimStatus] = useState({ assigned: false, assignedTo: null });
  // ── Profile pics — keyed by username, value = blob URL ──
  const [userPics, setUserPics] = useState({});
  const [adminPic, setAdminPic] = useState(null);

  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch and cache profile pic
  const loadPic = useCallback(async (username) => {
    if (!username || userPics[username] !== undefined) return;
    setUserPics(prev => ({ ...prev, [username]: null })); // placeholder
    const url = await fetchProfilePic(username);
    if (url) setUserPics(prev => ({ ...prev, [username]: url }));
  }, [userPics]);

  // Load admin's own pic on mount
  useEffect(() => {
    if (adminUsername) {
      fetchProfilePic(adminUsername).then(url => { if (url) setAdminPic(url); });
    }
  }, [adminUsername]);

  const th = {
    bg:      isDark ? "#0d1117" : "#f5f7fb",
    sidebar: isDark ? "#0f1117" : "#ffffff",
    panel:   isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    border:  isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)",
    header:  isDark ? "#0f1117" : "#ffffff",
    input:   isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
    text:    isDark ? "#e8e8f0" : "#0f0f1a",
    sub:     isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.4)",
  };

  const normalizeUsername = u => (u || "").toLowerCase().trim().replace(/^admin_/, "");
  const usernamesMatch = (a, b) => normalizeUsername(a) === normalizeUsername(b);

  const isViewer = (() => {
    if (isSuperAdmin) return false;
    if (claimLoading) return false;
    if (!claimStatus.assigned) return true;
    return !usernamesMatch(claimStatus.assignedTo, adminUsername);
  })();
  const isMyClaim = !isSuperAdmin && claimStatus.assigned && usernamesMatch(claimStatus.assignedTo, adminData?.username || adminUsername);
  const canSend = isSuperAdmin || isMyClaim;

  const fetchClaim = useCallback(async (username) => {
    try {
      setClaimLoading(true);
      const res = await fetch(`${chatMgmt}/ChatManagement/claim/${username}`);
      if (!res.ok) return;
      const data = await res.json();
      setClaimStatus({ assigned: !!data.assigned, assignedTo: data.assignedTo ?? null });
    } catch (e) { console.error(e); }
    finally { setClaimLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (username) => {
    if (!username) return;
    try {
      const res = await fetch(`${api}/Messages/admin/${adminUsername}/user/${username}`);
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    } catch (e) { console.error(e); }
  }, [adminUsername]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${api}/Messages/admin/${adminUsername}`);
      if (!res.ok) return;
      const data = await res.json();
      const grouped = {};
      data.forEach(msg => { if (!grouped[msg.user]) grouped[msg.user] = []; grouped[msg.user].push(msg); });
      const list = await Promise.all(
        Object.entries(grouped).map(async ([username, msgs]) => {
          const sorted = [...msgs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          let unreadCount = 0;
          try { const r = await fetch(`${api}/Messages/admin/${adminUsername}/user/${username}/unread`); if (r.ok) { const d = await r.json(); unreadCount = d.count ?? 0; } } catch {}
          return { username, displayName: username, lastMessage: sorted[0]?.content, lastTimestamp: sorted[0]?.timestamp, unreadCount, online: true };
        })
      );
      list.sort((a, b) => new Date(b.lastTimestamp) - new Date(a.lastTimestamp));
      setConversations(list);
      // Load pics for all users
      list.forEach(c => loadPic(c.username));
    } catch (e) { console.error(e); }
  }, [adminUsername, loadPic]);

  const fetchPin = useCallback(async (username) => {
    try { const res = await fetch(`${chatMgmt}/ChatManagement/pin/${username}`); if (!res.ok) return; const data = await res.json(); setPinnedMessageId(data.pinned ? data.messageId : null); } catch {}
  }, []);

  const markRead = useCallback(async (username) => {
    try { await fetch(`${api}/Messages/markAllAsRead/${username}`, { method: "POST" }); } catch {}
  }, []);

  const selectConversation = useCallback(async (username) => {
    setActiveUser(username); setReplyTo(null); setLoading(true);
    setPinnedMessageId(null); setClaimStatus({ assigned: false, assignedTo: null }); setClaimLoading(true);
    loadPic(username);
    await Promise.all([fetchClaim(username), fetchMessages(username), fetchPin(username), markRead(username)]);
    setLoading(false);
    setConversations(prev => prev.map(c => c.username === username ? { ...c, unreadCount: 0 } : c));
  }, [fetchClaim, fetchMessages, fetchPin, markRead, loadPic]);

  useEffect(() => {
    fetchConversations();
    pollRef.current = setInterval(() => {
      fetchConversations();
      if (activeUser) { fetchMessages(activeUser); fetchClaim(activeUser); }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchMessages, fetchClaim, activeUser]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (replyTo) inputRef.current?.focus(); }, [replyTo]);

  const claimConversation = async () => {
    if (!activeUser || claiming) return;
    const resolvedAdmin = adminData?.username || adminUsername;
    if (!resolvedAdmin) return;
    setClaiming(true);
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: activeUser, adminUsername: resolvedAdmin, role: adminRole }),
      });
      const data = await res.json();
      if (res.ok) setClaimStatus({ assigned: true, assignedTo: data.assignedTo ?? resolvedAdmin });
      else { alert(`⚠️ ${data.message}`); await fetchClaim(activeUser); }
    } catch (e) { console.error(e); }
    finally { setClaiming(false); }
  };

  const handleForward = async (targetAdmin) => {
    if (!targetAdmin || !activeUser) return;
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: activeUser, adminUsername: targetAdmin, role: "superadmin" }),
      });
      if (res.ok) { const data = await res.json(); setClaimStatus({ assigned: true, assignedTo: data.assignedTo ?? targetAdmin }); setShowForwardModal(false); }
      else { const err = await res.json().catch(() => ({})); alert(`❌ ${err.message ?? "Forward failed"}`); }
    } catch (e) { console.error(e); }
  };

  const handleUnassign = async () => {
    if (!activeUser) return;
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim/${encodeURIComponent(activeUser)}?role=superadmin`, { method: "DELETE" });
      if (res.ok) setClaimStatus({ assigned: false, assignedTo: null });
    } catch (e) { console.error(e); }
  };

  const handlePin = async (messageId) => {
    if (pinLoading) return;
    setPinLoading(true);
    const isUnpin = String(pinnedMessageId) === String(messageId);
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/pin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: activeUser, messageId: isUnpin ? null : String(messageId), pinnedBy: adminUsername, unpin: isUnpin }),
      });
      if (res.ok) setPinnedMessageId(isUnpin ? null : messageId);
    } catch (e) { console.error(e); }
    finally { setPinLoading(false); }
  };

  const handleDelete = async (messageId) => {
    try {
      await fetch(`${api}/Messages/${messageId}`, { method: "DELETE" });
      setMessages(prev => prev.filter(m => m.messageId !== messageId));
      if (String(pinnedMessageId) === String(messageId)) {
        await fetch(`${chatMgmt}/ChatManagement/pin`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: activeUser, messageId, pinnedBy: adminUsername, unpin: true }),
        });
        setPinnedMessageId(null);
      }
    } catch (e) { console.error(e); }
  };

  const handleReact = async (messageId, emoji) => {
    setLocalReactions(prev => ({ ...prev, [messageId]: prev[messageId] === emoji ? null : emoji }));
    try {
      await fetch(`${api}/Messages/react/${messageId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reactionType: emoji }),
      });
    } catch (e) { console.error(e); }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !activeUser || sending || !canSend) return;
    setSending(true);
    let finalContent = text;
    if (replyTo) {
      const q = replyTo.content?.startsWith("[IMAGE]") ? "📷 Photo" : replyTo.content?.slice(0, 60);
      finalContent = `↩ "${q}" — ${text}`;
    }
    try {
      const res = await fetch(`${api}/Messages/sendAdmin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: activeUser, admin: adminUsername, content: finalContent }),
      });
      if (res.ok) { setInput(""); setReplyTo(null); await fetchMessages(activeUser); await fetchConversations(); }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const sendImage = async () => {
    if (!imageFile || !activeUser || !canSend) return;
    setUploadingImage(true);
    try {
      const base64 = await new Promise(resolve => { const r = new FileReader(); r.onload = ev => resolve(ev.target.result); r.readAsDataURL(imageFile); });
      const fd = new FormData(); fd.append("file", imageFile);
      const uploadRes = await fetch(`${api}/Messages/uploadFile`, { method: "POST", body: fd });
      let fileName = imageFile.name;
      if (uploadRes.ok) { const d = await uploadRes.json(); fileName = d.fileName ?? imageFile.name; }
      await fetch(`${api}/Messages/sendAdmin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: activeUser, admin: adminUsername, content: `[IMAGE]${base64}`, senderType: "admin", fileName, fileType: imageFile.type, fileData: base64.split(",")[1], fileSize: imageFile.size }),
      });
      setImageFile(null); await fetchMessages(activeUser); await fetchConversations();
    } catch (err) { console.error(err); }
    finally { setUploadingImage(false); }
  };

  const startNewConversation = () => {
    const username = newUserInput.trim();
    if (!username) return;
    if (!conversations.find(c => c.username === username))
      setConversations(prev => [{ username, displayName: username, lastMessage: null, lastTimestamp: null, unreadCount: 0, online: false }, ...prev]);
    setActiveUser(username); setMessages([]); setNewUserInput(""); setShowNewConvo(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (imageFile) sendImage(); else sendMessage(); }
    if (e.key === "Escape") setReplyTo(null);
  };

  const filteredConvos = conversations.filter(c => (c.displayName || c.username).toLowerCase().includes(search.toLowerCase()));
  const canSendInput = (input.trim() || imageFile) && !sending && !uploadingImage && canSend;
  const pinnedMsg = pinnedMessageId ? messages.find(m => String(m.messageId) === String(pinnedMessageId)) : null;
  const mergedMessages = messages.map(m => ({
    ...m,
    reaction: localReactions[m.messageId] !== undefined ? localReactions[m.messageId] : m.reactionType ? m.reactionType : m.likes ? "❤️" : null,
  }));

  return (
    <div style={{
      display: "flex", height: "100%",
      fontFamily: "'SF Pro Text', -apple-system, 'Segoe UI', sans-serif",
      background: th.bg, overflow: "hidden",
    }}>
      <style>{`
        @keyframes spin        { to { transform: rotate(360deg); } }
        @keyframes fade-in     { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slide-up    { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slide-in    { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes msg-in      { from { opacity: 0; transform: scale(0.92) translateY(6px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes pop-spring  { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes wiggle      { 0%,100%{transform:rotate(0)}30%{transform:rotate(-12deg)}60%{transform:rotate(10deg)} }
        @keyframes pulse-green { 0%,100%{box-shadow:0 0 0 0 rgba(49,164,76,0.5)}70%{box-shadow:0 0 0 5px rgba(49,164,76,0)} }
        @keyframes pulse-blue  { 0%,100%{box-shadow:0 0 0 0 rgba(0,132,255,0.4)}70%{box-shadow:0 0 0 8px rgba(0,132,255,0)} }
        @keyframes glow-bounce { 0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-8px) scale(1.06)} }
        @keyframes typing-dot  { 0%,80%,100%{transform:scale(0.6);opacity:0.4}40%{transform:scale(1);opacity:1} }
        ::-webkit-scrollbar      { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {showForwardModal && (
        <ForwardModal username={activeUser} currentAssignee={claimStatus.assignedTo}
          onForward={handleForward} onClose={() => setShowForwardModal(false)} isDark={isDark} />
      )}

      {/* ══════════════ SIDEBAR ══════════════ */}
      <div style={{
        width: 280, display: "flex", flexDirection: "column",
        background: th.sidebar,
        borderRight: `1px solid ${th.border}`,
        flexShrink: 0,
      }}>
        {/* Sidebar Header */}
        <div style={{
          padding: "18px 16px 12px",
          background: th.sidebar,
          borderBottom: `1px solid ${th.border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            {/* Admin identity */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={adminUsername} size={40} online picUrl={adminPic} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: th.text, lineHeight: 1.2 }}>
                  {adminData?.fullNames || adminUsername}
                </div>
                <div style={{ fontSize: 10, color: th.sub, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#31a24c", display: "inline-block", animation: "pulse-green 2s infinite" }} />
                  {isSuperAdmin ? (
                    <span style={{ background: "linear-gradient(135deg,#f7971e,#f44336)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontWeight: 800, fontSize: 10 }}>SUPERADMIN</span>
                  ) : "Online"}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setIsDark(v => !v)}
                style={{ background: th.input, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}
                onMouseLeave={e => e.currentTarget.style.background = th.input}
              >{isDark ? "☀️" : "🌙"}</button>
              <button onClick={() => setShowNewConvo(v => !v)}
                style={{ background: showNewConvo ? "rgba(0,132,255,0.2)" : th.input, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, transition: "all 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(0,132,255,0.2)"}
                onMouseLeave={e => e.currentTarget.style.background = showNewConvo ? "rgba(0,132,255,0.2)" : th.input}
              >✏️</button>
            </div>
          </div>

          {/* New conversation input */}
          {showNewConvo && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10, animation: "slide-up 0.2s ease" }}>
              <input value={newUserInput} onChange={e => setNewUserInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && startNewConversation()}
                placeholder="Enter username..." autoFocus
                style={{ flex: 1, borderRadius: 20, border: "1.5px solid #0084ff", padding: "8px 14px", fontSize: 13, outline: "none", background: th.input, color: th.text, fontFamily: "inherit" }} />
              <button onClick={startNewConversation}
                style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,132,255,0.4)" }}>
                Go
              </button>
            </div>
          )}

          {/* Search */}
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, opacity: 0.5 }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations..."
              style={{ width: "100%", boxSizing: "border-box", background: th.input, border: "none", borderRadius: 22, padding: "9px 14px 9px 34px", fontSize: 13, outline: "none", color: th.text, fontFamily: "inherit" }} />
          </div>
        </div>

        {/* Conversation list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px 0" }}>
          {filteredConvos.length === 0 ? (
            <div style={{ textAlign: "center", color: th.sub, padding: "32px 16px", fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>💬</div>
              No conversations yet
            </div>
          ) : filteredConvos.map((conv, i) => (
            <div key={conv.username} style={{ animation: `slide-in 0.2s ease ${i * 0.03}s both` }}>
              <ConversationItem conv={conv} active={activeUser === conv.username}
                onClick={() => selectConversation(conv.username)}
                isDark={isDark} picUrl={userPics[conv.username]} />
            </div>
          ))}
        </div>

        {/* Heading */}
        <div style={{ padding: "10px 16px 6px", borderTop: `1px solid ${th.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: th.sub, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            💬 {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* ══════════════ CHAT AREA ══════════════ */}
      {activeUser ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: th.bg, minWidth: 0 }}>

          {/* Chat Header */}
          <div style={{
            padding: "12px 20px",
            borderBottom: `1px solid ${th.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: th.header,
            backdropFilter: "blur(20px)",
            flexWrap: "wrap", gap: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar name={activeUser} size={46} online picUrl={userPics[activeUser]} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: th.text }}>
                  {conversations.find(c => c.username === activeUser)?.displayName || activeUser}
                </div>
                <div style={{ fontSize: 11, color: "#31a24c", display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ animation: "pulse-green 2s infinite", width: 6, height: 6, borderRadius: "50%", background: "#31a24c", display: "inline-block" }} />
                  Active now
                </div>
              </div>
            </div>

            {/* Claim / assign buttons */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {isSuperAdmin ? (
                <>
                  <div style={{
                    background: claimStatus.assigned ? "rgba(34,197,94,0.1)" : "rgba(245,158,11,0.1)",
                    border: `1.5px solid ${claimStatus.assigned ? "rgba(34,197,94,0.4)" : "rgba(245,158,11,0.4)"}`,
                    borderRadius: 20, padding: "7px 14px", fontSize: 12, fontWeight: 600,
                    color: claimStatus.assigned ? "#22c55e" : "#f59e0b",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    {claimStatus.assigned ? <>✅ <strong>{claimStatus.assignedTo}</strong></> : "⚠️ Unassigned"}
                  </div>
                  <button onClick={() => setShowForwardModal(true)}
                    style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 12px rgba(99,102,241,0.4)", transition: "all 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                  >✏️ {claimStatus.assigned ? "Change" : "Assign"}</button>
                  <button onClick={() => setShowForwardModal(true)}
                    style={{ background: "linear-gradient(135deg,#f7971e,#f44336)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 10px rgba(247,151,30,0.4)", transition: "all 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                  >⚡ Forward</button>
                  {claimStatus.assigned && (
                    <button onClick={handleUnassign}
                      style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 20, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.2)"}
                      onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.1)"}
                    >🗑️ Unassign</button>
                  )}
                </>
              ) : claimLoading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: th.sub }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid transparent", borderTop: "2px solid #0084ff", animation: "spin 0.8s linear infinite" }} />
                  Checking...
                </div>
              ) : !claimStatus.assigned ? (
                <button onClick={claimConversation} disabled={claiming}
                  style={{ background: claiming ? th.input : "linear-gradient(135deg,#0084ff,#006fde)", color: claiming ? th.sub : "#fff", border: "none", borderRadius: 20, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: claiming ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: claiming ? "none" : "0 3px 12px rgba(0,132,255,0.45)", animation: claiming ? "none" : "pulse-blue 2s infinite", transition: "all 0.2s" }}>
                  {claiming ? <><div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 0.8s linear infinite" }} />Claiming...</> : "🙋 Claim Chat"}
                </button>
              ) : isMyClaim ? (
                <div style={{ background: "rgba(34,197,94,0.1)", border: "1.5px solid rgba(34,197,94,0.4)", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: "#22c55e", animation: "pop-spring 0.3s ease" }}>
                  ✅ Claimed by me
                </div>
              ) : (
                <div style={{ background: "rgba(247,151,30,0.1)", border: "1.5px solid rgba(247,151,30,0.4)", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#f7971e" }}>
                  🔒 {claimStatus.assignedTo}
                </div>
              )}
            </div>
          </div>

          {/* Viewer / locked banner */}
          {isViewer && !claimLoading && (
            <div style={{
              padding: "12px 20px",
              background: !claimStatus.assigned ? "rgba(0,132,255,0.06)" : "rgba(247,151,30,0.06)",
              borderBottom: `2px solid ${!claimStatus.assigned ? "rgba(0,132,255,0.4)" : "rgba(247,151,30,0.4)"}`,
              display: "flex", alignItems: "center", gap: 12,
              animation: "slide-up 0.2s ease",
            }}>
              <span style={{ fontSize: 22 }}>{!claimStatus.assigned ? "🔒" : "👁️"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: !claimStatus.assigned ? "#0084ff" : "#f7971e", marginBottom: 2 }}>
                  {!claimStatus.assigned ? "Unclaimed conversation" : `Handled by ${claimStatus.assignedTo}`}
                </div>
                <div style={{ fontSize: 11, color: th.sub }}>
                  {!claimStatus.assigned ? "Claim this chat to start replying." : "You are in view-only mode."}
                </div>
              </div>
              {!claimStatus.assigned && (
                <button onClick={claimConversation} disabled={claiming}
                  style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 16, padding: "9px 20px", fontSize: 13, fontWeight: 700, cursor: claiming ? "wait" : "pointer", boxShadow: "0 2px 10px rgba(0,132,255,0.4)", whiteSpace: "nowrap" }}>
                  {claiming ? "..." : "🙋 Claim Now"}
                </button>
              )}
            </div>
          )}

          {/* Pinned message banner */}
          {pinnedMsg && (
            <div style={{ padding: "9px 20px", background: isDark ? "rgba(247,151,30,0.06)" : "rgba(247,151,30,0.05)", borderBottom: `1px solid rgba(247,151,30,0.25)`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ animation: "wiggle 2s infinite", fontSize: 14 }}>📌</span>
              <span style={{ flex: 1, fontSize: 13, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <strong>Pinned:</strong> {pinnedMsg.content?.startsWith("[IMAGE]") ? "📷 Photo" : pinnedMsg.content?.replace(/^↩ ".*?" — /, "")}
              </span>
              <button onClick={() => handlePin(pinnedMsg.messageId)} disabled={pinLoading}
                style={{ background: "none", border: "none", cursor: "pointer", color: th.sub, fontSize: 16, padding: 4, borderRadius: "50%", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >✕</button>
            </div>
          )}

          {/* ── Messages ── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", minHeight: 0, background: th.bg }}>
            {loading ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, color: th.sub }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#0084ff", animation: `typing-dot 1.2s infinite ${i * 0.2}s` }} />
                  ))}
                </div>
                <span style={{ fontSize: 13 }}>Loading messages...</span>
              </div>
            ) : mergedMessages.length === 0 ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
                <div style={{ animation: "glow-bounce 2.5s ease-in-out infinite" }}>
                  <Avatar name={activeUser} size={90} picUrl={userPics[activeUser]} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 20, color: th.text }}>{activeUser}</div>
                <div style={{ fontSize: 13, color: th.sub }}>
                  {isViewer && !claimLoading ? "👁️ View-only mode" : "Start the conversation 👋"}
                </div>
              </div>
            ) : (
              mergedMessages.map((msg, i) => {
                const showDay = i === 0 || formatDay(mergedMessages[i - 1]?.timestamp) !== formatDay(msg.timestamp);
                return (
                  <div key={msg.messageId ?? i}>
                    {showDay && (
                      <div style={{ textAlign: "center", margin: "14px 0 8px" }}>
                        <span style={{ background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)", backdropFilter: "blur(10px)", color: th.sub, fontSize: 11, fontWeight: 700, padding: "4px 14px", borderRadius: 20 }}>
                          {formatDay(msg.timestamp)}
                        </span>
                      </div>
                    )}
                    <MessageBubble
                      msg={msg} isAdmin={msg.senderType === "admin"}
                      prevMsg={mergedMessages[i - 1]} nextMsg={mergedMessages[i + 1]}
                      onReply={setReplyTo} onDelete={handleDelete} onPin={handlePin} onReact={handleReact}
                      isDark={isDark} pinnedId={pinnedMessageId} activeUser={activeUser}
                      userPicUrl={userPics[msg.user]}
                    />
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Reply bar */}
          {replyTo && canSend && <ReplyBar replyTo={replyTo} onCancel={() => setReplyTo(null)} isDark={isDark} />}

          {/* Image preview */}
          {imageFile && canSend && (
            <div style={{ padding: "8px 18px 4px", borderTop: `1px solid ${th.border}`, background: th.header }}>
              <ImagePreview file={imageFile} onRemove={() => setImageFile(null)} />
            </div>
          )}

          {/* Input area */}
          <div style={{
            padding: "12px 18px",
            borderTop: `1px solid ${th.border}`,
            display: "flex", alignItems: "flex-end", gap: 10,
            background: th.header,
            opacity: canSend ? 1 : 0.4,
            pointerEvents: canSend ? "auto" : "none",
            filter: canSend ? "none" : "grayscale(80%)",
            transition: "opacity 0.3s, filter 0.3s",
          }}>
            {/* Image attach */}
            <button onClick={() => fileRef.current?.click()}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: "50%", display: "flex", alignItems: "center", flexShrink: 0, transition: "background 0.15s, transform 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,132,255,0.15)"; e.currentTarget.style.transform = "scale(1.15)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.transform = "scale(1)"; }}>
              <svg width="22" height="22" fill="none" stroke="#0084ff" viewBox="0 0 24 24" strokeWidth={1.8}>
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="#0084ff" stroke="none" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" />
              </svg>
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) setImageFile(f); e.target.value = ""; }} />

            {/* Text input */}
            <div style={{ flex: 1, background: th.input, borderRadius: 24, padding: "10px 18px", display: "flex", alignItems: "center", border: `1px solid ${th.border}`, transition: "border-color 0.15s" }}
              onFocusCapture={e => e.currentTarget.style.borderColor = "rgba(0,132,255,0.4)"}
              onBlurCapture={e => e.currentTarget.style.borderColor = th.border}>
              <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder={!canSend ? "🔒 " + (!claimStatus.assigned ? "Claim chat first..." : `Only ${claimStatus.assignedTo} can reply...`) : imageFile ? "Add a caption..." : replyTo ? "Write a reply..." : `Message ${activeUser}...`}
                rows={1} style={{ flex: 1, border: "none", outline: "none", background: "transparent", resize: "none", fontSize: 14, color: th.text, lineHeight: 1.5, maxHeight: 120, overflow: "auto", fontFamily: "inherit" }} />
            </div>

            {/* Send */}
            {!canSendInput ? (
              <button onClick={sendMessage} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 24, padding: 4, transition: "transform 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.2)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>👍</button>
            ) : (
              <button onClick={imageFile ? sendImage : sendMessage} disabled={!canSendInput}
                style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", border: "none", borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,132,255,0.5)", flexShrink: 0, transition: "transform 0.15s, box-shadow 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 6px 22px rgba(0,132,255,0.65)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,132,255,0.5)"; }}>
                {sending || uploadingImage
                  ? <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2.5px solid rgba(255,255,255,0.3)", borderTop: "2.5px solid #fff", animation: "spin 0.8s linear infinite" }} />
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>}
              </button>
            )}
          </div>
        </div>

      ) : (
        /* ── Empty State ── */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, background: th.bg, padding: 40 }}>
          {/* Animated Messenger icon */}
          <div style={{ position: "relative", marginBottom: 8 }}>
            <div style={{
              width: 96, height: 96, borderRadius: "50%",
              background: "linear-gradient(135deg, #0084ff 0%, #00c6ff 50%, #0084ff 100%)",
              backgroundSize: "200% 200%",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 12px 40px rgba(0,132,255,0.5), 0 0 0 0 rgba(0,132,255,0.3)",
              animation: "glow-bounce 3s ease-in-out infinite, pulse-blue 2s infinite",
            }}>
              <svg width="52" height="52" viewBox="0 0 48 48" fill="white">
                <path d="M24 4C12.95 4 4 12.28 4 22.5c0 5.63 2.68 10.66 6.9 14.07V44l6.77-3.72A21.6 21.6 0 0024 41c11.05 0 20-8.28 20-18.5S35.05 4 24 4zm2.1 24.92l-5.1-5.44-9.95 5.44 10.95-11.63 5.23 5.44 9.82-5.44-10.95 11.63z" />
              </svg>
            </div>
            {/* Orbiting dots */}
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                position: "absolute",
                width: 10, height: 10, borderRadius: "50%",
                background: ["#0084ff", "#00c6ff", "#006fde"][i],
                top: "50%", left: "50%",
                transform: `rotate(${i * 120}deg) translateX(60px) translateY(-50%)`,
                animation: `spin ${2.5 + i * 0.5}s linear infinite`,
                opacity: 0.6,
                boxShadow: `0 2px 8px rgba(0,132,255,0.6)`,
              }} />
            ))}
          </div>

          <div style={{ textAlign: "center" }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: th.text }}>Messages</h2>
            <p style={{ margin: 0, fontSize: 14, color: th.sub, maxWidth: 260, lineHeight: 1.6 }}>
              Select a conversation from the sidebar or start a new one
            </p>
          </div>

          <button onClick={() => setShowNewConvo(true)}
            style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 28, padding: "13px 32px", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 20px rgba(0,132,255,0.5)", letterSpacing: "0.02em", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px) scale(1.03)"; e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,132,255,0.6)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,132,255,0.5)"; }}>
            ✉️ New Message
          </button>
        </div>
      )}
    </div>
  );
}